import axios from "axios";
import { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import img1 from '../../../Assets/Images/contact2.jpg';
import img2 from '../../../Assets/Images/contact1.jpg';


// ─── Shared hook ────────────────────────────────────────────────────────────
function useInView() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, visible];
}

const projectTypes = [
  "General Construction",
  "Architectural Design",
  "Project Management",
  "Renovation & Remodeling",
  "Consultation & Planning",
  "Other",
];

function Input({
  label,
  placeholder,
  type = "text",
  required = true,
  name,
  value,
  onChange,
}) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {label} {required && <span className="text-green-500">*</span>}
      </label>
      <input
        type={type}
        placeholder={placeholder}
        name={name}
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className={`w-full px-4 py-3 rounded-xl text-sm text-gray-800 outline-none bg-white transition-all duration-300 border-[1.5px] ${focused ? "border-green-500 ring-4 ring-green-100 shadow-sm" : "border-slate-200 shadow-sm"}`}
      />
    </div>
  );
}

function SelectField({ label, required = true, name, value, onChange }) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {label} {required && <span className="text-green-500">*</span>}
      </label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        defaultValue=""
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className={`w-full px-4 py-3 rounded-xl text-sm text-gray-500 outline-none bg-white cursor-pointer appearance-none transition-all duration-300 border-[1.5px] ${focused ? "border-green-500 ring-4 ring-green-100 shadow-sm" : "border-slate-200 shadow-sm"}`}
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%236b7280'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 12px center",
          backgroundSize: "20px",
        }}
      >
        <option value="" disabled>
          Select
        </option>
        {projectTypes.map((p) => (
          <option key={p} value={p}>
            {p}
          </option>
        ))}
      </select>
    </div>
  );
}

function TextareaField({ label, placeholder, name, value, onChange }) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}
      </label>
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        rows={5}
        placeholder={placeholder}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className={`w-full px-4 py-3 rounded-xl text-sm text-gray-800 outline-none bg-white resize-y transition-all duration-300 border-[1.5px] ${focused ? "border-green-500 ring-4 ring-green-100 shadow-sm" : "border-slate-200 shadow-sm"}`}
      />
    </div>
  );
}

const GetInvolved = () => {
  const [leftRef, leftVisible] = useInView();
  const [formRef, formVisible] = useInView();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    number: "",
    projectType: "",
    query: "",
  });

  const [errors, setErrors] = useState({});

  const handle = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: "" });
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Invalid email";
    if (!form.number.trim()) e.number = "Phone number is required";
    else if (!/^\+?[\d\s\-]{8,15}$/.test(form.number))
      e.number = "Invalid phone number";
    if (!form.projectType) e.projectType = "Select a project type";
    if (!form.query.trim()) e.query = "Message is required";
    return e;
  };

  const handleSubmit = async () => {
    try {
      const e = validate();
      if (Object.keys(e).length > 0) {
        setErrors(e);
        return;
      }
      setSending(true);
      await axios.post(`${process.env.REACT_APP_API_URL}/enquiry/create`, form);

      toast.success("Inquiry submitted successfully!");
      reset();
    } catch (error) {
      console.log("Error", error);
    } finally {
      setSending(false);
      setSent(true);
    }
  };

  const reset = () => {
    setForm({ name: "", email: "", number: "", projectType: "", query: "" });
    setErrors({});
    setSent(false);
  };

  return (
    <div className="flex items-center justify-center py-20 px-6 md:px-10 lg:px-12 xl:px-20  bg-gradient-to-br from-slate-100 via-blue-50 to-slate-100 relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(circle,#cbd5e1 1px,transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />
      <div className="relative w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-start">
          <div
            ref={leftRef}
            className={`transition-all duration-700 ${leftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-6 bg-green-400" />
              <span className="text-green-500 font-bold text-xs tracking-widest uppercase">
                Get Involved
              </span>
            </div>
            <h2 className="font-bold uppercase text-gray-900 tracking-wider text-2xl md:text-4xl lg:text-[45px] font-rajdhani leading-9  mb-6">
              Let's{" "}
              <span
                className="text-transparent"
                style={{ WebkitTextStroke: "2px #0f1f4b" }}
              >
                Build
              </span>{" "}
              Something
              <br />
              Great Together Today
            </h2>
            <div className="flex flex-wrap gap-x-5 gap-y-2 mb-10">
              {["24/7 Support", "Free Consultation", "Certified Experts"].map(
                (p) => (
                  <div key={p} className="flex items-center gap-1.5">
                    <svg
                      viewBox="0 0 24 24"
                      fill="#89ea5f"
                      className="w-4 h-4 flex-shrink-0"
                    >
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                    </svg>
                    <span className="text-gray-600 text-sm font-medium">
                      {p}
                    </span>
                  </div>
                ),
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                img2,
                img1,
              ].map((src, i) => (
                <div
                  key={i}
                  className={`rounded-2xl overflow-hidden group h-48 shadow-lg transition-all duration-700 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                  style={{ transitionDelay: `${0.2 + i * 0.15}s` }}
                >
                  <img
                    src={src}
                    alt=""
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
              ))}
            </div>
          </div>
          <div
            ref={formRef}
            className={`rounded-2xl p-8 bg-white/70 backdrop-blur-sm border border-white/90 shadow-xl transition-all duration-700 delay-200 ${formVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
              <Input
                label="Full Name"
                placeholder="e.g. Patrick Johnson"
                name="name"
                value={form.name}
                onChange={handle}
              />
              <Input
                label="Email Address"
                placeholder="e.g. hello@steelmind.com"
                type="email"
                name="email"
                value={form.email}
                onChange={handle}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
              <Input
                label="Phone Number"
                placeholder="(+62) 1234 5678 9010"
                type="tel"
                name="number"
                value={form.number}
                onChange={handle}
              />
              <SelectField
                label="Project Type"
                name="projectType"
                value={form.projectType}
                onChange={handle}
              />
            </div>
            <div className="mb-7">
              <TextareaField
                name="query"
                value={form.query}
                onChange={handle}
                label="Message"
                placeholder="Write your message here ..."
              />
            </div>
            {sent ? (
              <div className="flex items-center gap-3 bg-green-50 border border-green-200 text-green-700 px-6 py-4 rounded-xl text-sm font-medium">
                <svg
                  viewBox="0 0 24 24"
                  fill="#16a34a"
                  className="w-5 h-5 flex-shrink-0"
                >
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                </svg>
                Message sent! We'll get back to you soon.
              </div>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={sending}
                className="group relative overflow-hidden flex items-center gap-3 bg-green-500 text-white font-black uppercase text-sm px-7 py-4 rounded-xl hover:shadow-xl hover:shadow-green-500/30 transition-shadow duration-300 disabled:opacity-70"
              >
                <span className="relative z-10">
                  {sending ? "Sending..." : "Send Message"}
                </span>
                {sending ? (
                  <span className="relative z-10 w-7 h-7 bg-white/20 rounded-lg flex items-center justify-center">
                    <svg
                      className="animate-spin w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" strokeOpacity="0.3" />
                      <path d="M12 2a10 10 0 0 1 10 10" />
                    </svg>
                  </span>
                ) : (
                  <span className="relative z-10 w-7 h-7 bg-white/20 rounded-lg flex items-center justify-center group-hover:translate-x-1 transition-transform duration-300">
                    →
                  </span>
                )}
                <div className="absolute inset-0 bg-gray-900 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 z-0" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GetInvolved;
